import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { AIConversation } from '../../extViewmodels/sdk/models/AI';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'sales-live-coaching-conversations';

type QueryParams = {
	enabled?: boolean;
	refetchInterval?: number | false;
	impersonationContext?: Api.IImpersonationContext;
};

/**
 * Fetches every `SalesLiveCoaching` AI conversation for the current (or impersonated) account.
 *
 * Unlike `useTysonCoachingConversations`, which scopes results to the signed-in user via a
 * `CreatorId` criterion, this hook intentionally OMITS that criterion so the result is
 * account-wide: it returns conversations created by ALL reps in the account. Intended for the
 * internal-only Coffee page that lists an account's coaching conversations.
 */
export function useSalesLiveCoachingConversationsQuery(options?: QueryParams) {
	const userSession = useUserSession();
	const accountId = userSession.account?.id;

	const impersonationContext = options?.impersonationContext;
	const impersonationKey = impersonationContext
		? { accountId: impersonationContext.account?.id, userId: impersonationContext.user?.id }
		: undefined;

	const filter: Api.AIConversationFilter = {
		criteria: [
			{
				property: Api.AIConversationFilterProperty.ConversationType,
				value: Api.AIConversationType.SalesLiveCoaching,
			},
		],
		op: Api.FilterOperator.And,
	};

	return useQuery({
		queryKey: [BASE_QUERY_KEY, accountId, impersonationKey],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<AIConversation[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'aiconversation/filter',
					impersonationContext,
				}),
				'POST',
				filter
			),
		enabled: options?.enabled ?? true,
		refetchInterval: options?.refetchInterval,
		meta: {
			toastErrorMessage: 'Failed to load coaching conversations',
			errorLogging: { fn: 'useSalesLiveCoachingConversationsQuery' },
		},
	});
}
